import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  ShieldCheck,
  Sun,
  Droplets,
  Factory,
  Leaf,
  Menu,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = "https://customer-assets.emergentagent.com/job_tank-showcase/artifacts/dfuf65tx_exact%20sized%20transaprent%20file.png";
const HERO_TANK = "https://customer-assets.emergentagent.com/job_505f2641-60ba-4f8b-b71e-1a6facdf5482/artifacts/nqgl1squ_Tank%20with%20Teardrop.png";
const TANK_TOP = "https://customer-assets.emergentagent.com/job_505f2641-60ba-4f8b-b71e-1a6facdf5482/artifacts/wl4pchd9_Tank%20Top%20View.png";
const TANKS_NATURE = "https://customer-assets.emergentagent.com/job_505f2641-60ba-4f8b-b71e-1a6facdf5482/artifacts/bxaxtgv2_Tanks%20outside.png";
const TANKS_DISPATCH = "https://customer-assets.emergentagent.com/job_505f2641-60ba-4f8b-b71e-1a6facdf5482/artifacts/2qdz3jp7_Tanks%20waiting%20dispatch.png";

const WHATSAPP_NUMBER = "263774724222";
const PHONE_DISPLAY = "+263 774 724 222";
const PHONE_SECONDARY = "+263 71 945 6477";
const EMAIL = "info@mutumwa.co.zw";
const LOCATION = "1 Harvey Brown Ave, Milton Park, Harare, Zimbabwe";

const NAV = [
  { href: "#products", label: "Products" },
  { href: "#why", label: "Why Mutumwa" },
  { href: "#gallery", label: "Gallery" },
  { href: "/borehole-drilling", label: "Borehole Drilling", route: true },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

// ─────────────────────────────────────────────────────────────
// Reveal hook
// ─────────────────────────────────────────────────────────────
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

// ─────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────
const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#F7F5F0]/80 border-b border-[rgba(26,47,76,0.12)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO} alt="Mutumwa" className="h-12 w-auto" />
        </a>
        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.href}
                to={n.href}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="link-underline text-xs uppercase tracking-[0.18em] font-semibold text-[#1A2F4C]"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="link-underline text-xs uppercase tracking-[0.18em] font-semibold text-[#1A2F4C]"
              >
                {n.label}
              </a>
            )
          )}
        </nav>
        <div className="hidden lg:block">
          <a href="#contact" data-testid="header-quote-btn" className="btn-primary">
            Get a Quote <ArrowRight size={16} />
          </a>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[#1A2F4C]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[rgba(26,47,76,0.12)] bg-[#F7F5F0]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.18em] font-semibold text-[#1A2F4C]"
              >
                {n.label}
              </a>
            ))}
            <a href="#contact" onClick={() => setOpen(false)} className="btn-primary w-fit">
              Get a Quote <ArrowRight size={16} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

// ─────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────
const Hero = () => {
  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello Mutumwa, I'd like a quote for your tanks.")}`;
  return (
    <section id="top" data-testid="hero-section" className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center min-h-[78vh]">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-8 reveal">
              <span className="h-[1px] w-10 bg-[#8B1E28]" />
              <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
                Tank Manufacturing · Est. Africa
              </span>
            </div>
            <h1
              data-testid="hero-heading"
              className="font-display font-black text-[#1A2F4C] tracking-tighter leading-[0.88] text-5xl md:text-6xl lg:text-[5.25rem] reveal"
            >
              Built for Africa's <em className="font-light italic">Harvest.</em>
              <br />
              Engineered for <em className="font-light italic">Life.</em>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-[#4A4E58] leading-relaxed reveal">
              Mutumwa manufactures heavy-duty water tanks, agricultural storage, and industrial
              liquid vessels — rotomoulded for the African sun, sized for the work ahead.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 reveal">
              <a href="#contact" data-testid="hero-quote-btn" className="btn-primary">
                Get a Quote <ArrowRight size={16} />
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="btn-outline"
              >
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg reveal">
              {[
                { k: "100+", v: "Tanks delivered" },
                { k: "12yr", v: "UV-resistant build" },
                { k: "100%", v: "Food-grade resin" },
              ].map((s) => (
                <div key={s.v} className="border-t-2 border-[#1A2F4C] pt-3">
                  <div className="font-display font-black text-3xl md:text-4xl text-[#1A2F4C]">
                    {s.k}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-[#4A4E58] mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 reveal">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#EBE7DE] -z-10 translate-x-4 translate-y-4" />
              <img
                src={HERO_TANK}
                alt="Mutumwa Agri water tank in field"
                className="w-full h-[420px] md:h-[560px] object-cover grayscale-0"
                data-testid="hero-image"
              />
              <div className="absolute bottom-4 left-4 bg-[#F7F5F0] px-4 py-2 border border-[#1A2F4C]">
                <span className="font-display italic text-[#1A2F4C] text-sm">Mutumwa Agri · 5000L</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-16 md:mt-24 border-y border-[rgba(26,47,76,0.15)] py-6 overflow-hidden">
        <div className="marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10 pr-10 font-display italic text-[#1A2F4C]/40 text-3xl md:text-5xl whitespace-nowrap">
              {["5 000L", "10 000L", "20 000L", "Custom Builds"].map((c) => (
                <React.Fragment key={c + i}>
                  <span>{c}</span>
                  <span className="text-[#8B1E28]">◆</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Products
// ─────────────────────────────────────────────────────────────
const PRODUCTS = [
  {
    id: "water",
    title: "Water Tanks",
    tag: "Residential & Commercial",
    copy: "Food-grade, UV-stabilised polyethylene storage for homes, schools, clinics and businesses. Ribbed construction for structural strength.",
    capacities: "5 000L · 10 000L · 20 000L",
    icon: Droplets,
    image: TANKS_DISPATCH,
    span: "md:col-span-7 md:row-span-2",
  },
  {
    id: "agri",
    title: "Agri Tanks",
    tag: "Farm & Irrigation",
    copy: "Purpose-built bulk water storage for livestock, irrigation, and greenhouse operations across Southern Africa.",
    capacities: "5 000L · 10 000L · 20 000L",
    icon: Leaf,
    image: TANKS_NATURE,
    span: "md:col-span-5",
  },
  {
    id: "industrial",
    title: "Industrial Liquids",
    tag: "Chemical & Fuel Storage",
    copy: "Chemically resistant storage for fertiliser, detergents, effluent and non-flammable industrial fluids — custom dimensions available.",
    capacities: "5 000L · 10 000L · 20 000L",
    icon: Factory,
    image: TANK_TOP,
    span: "md:col-span-5",
  },
];

const Products = () => {
  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-36 bg-[#F7F5F0] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 reveal">
          <div>
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
              / 01 — Product Range
            </span>
            <h2 className="font-display font-black text-4xl md:text-6xl text-[#1A2F4C] tracking-tight leading-none mt-4 max-w-2xl">
              Storage that matches the <em className="italic font-light">scale of your work.</em>
            </h2>
          </div>
          <p className="max-w-sm text-[#4A4E58]">
            Three product families, dozens of sizes. Every Mutumwa tank is rotomoulded in one
            seamless piece — no welds, no weak points.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-fr">
          {PRODUCTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className={`product-card reveal bg-[#EBE7DE] border border-[rgba(26,47,76,0.15)] p-8 md:p-10 flex flex-col justify-between min-h-[380px] relative overflow-hidden ${p.span}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div
                  className="absolute inset-0 opacity-30 pointer-events-none"
                  style={{
                    backgroundImage: `url(${p.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    mixBlendMode: "multiply",
                  }}
                />
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Icon size={22} className="text-[#8B1E28]" />
                      <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                        {p.tag}
                      </span>
                    </div>
                    <span className="font-display italic text-[#1A2F4C]/70 text-sm">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display font-black text-3xl md:text-5xl text-[#1A2F4C] tracking-tight leading-none mt-6">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[#2C303A] max-w-md">{p.copy}</p>
                </div>
                <div className="relative flex items-end justify-between mt-10">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A4E58]">
                      Capacity range
                    </div>
                    <div className="font-display text-xl text-[#1A2F4C] mt-1">{p.capacities}</div>
                  </div>
                  <a
                    href="#contact"
                    data-testid={`product-enquire-${p.id}`}
                    className="link-underline flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]"
                  >
                    Enquire <ArrowUpRight size={14} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Why Mutumwa
// ─────────────────────────────────────────────────────────────
const WHY = [
  {
    n: "01",
    title: "UV-Stabilised Polymer",
    body: "Engineered to hold its integrity under direct African sun for decades — not seasons.",
    icon: Sun,
  },
  {
    n: "02",
    title: "Food-Grade & Safe",
    body: "Every Mutumwa tank is rotomoulded from certified food-grade resin — safe for drinking water.",
    icon: ShieldCheck,
  },
  {
    n: "03",
    title: "Made in Zimbabwe",
    body: "Proudly Zimbabwean-manufactured with local jobs, local expertise, and shorter supply chains.",
    icon: Leaf,
  },
];

const Why = () => {
  return (
    <section id="why" data-testid="why-section" className="py-24 md:py-36 bg-[#1A2F4C] text-[#F7F5F0] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl reveal">
          <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#E6B8BC]">
            / 02 — Why Mutumwa
          </span>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[0.95] mt-4">
            Strength isn't a feature. <em className="italic font-light">It's the foundation.</em>
          </h2>
          <p className="mt-6 text-lg text-[#F7F5F0]/70 max-w-2xl">
            From drought-prone farms to busy industrial yards, our tanks are trusted because they
            are specified, engineered and finished with zero shortcuts.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 mt-16 md:mt-24">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <div
                key={w.n}
                data-testid={`why-item-${w.n}`}
                className="reveal border-t-2 border-[#F7F5F0]/30 pt-8"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display italic text-5xl md:text-6xl text-[#F7F5F0]/80">
                    {w.n}
                  </span>
                  <Icon size={28} className="text-[#E6B8BC]" />
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl mt-6">{w.title}</h3>
                <p className="mt-4 text-[#F7F5F0]/70 leading-relaxed">{w.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Gallery
// ─────────────────────────────────────────────────────────────
const Gallery = () => {
  const items = [
    { src: TANKS_DISPATCH, span: "md:col-span-8 md:row-span-2", h: "h-[360px] md:h-[620px]", label: "Dispatch yard" },
    { src: HERO_TANK, span: "md:col-span-4", h: "h-[360px] md:h-[300px]", label: "Mutumwa Agri in field" },
    { src: TANKS_NATURE, span: "md:col-span-4", h: "h-[360px] md:h-[300px]", label: "Outdoor install" },
    { src: TANK_TOP, span: "md:col-span-12", h: "h-[260px] md:h-[380px]", label: "Reinforced top-view" },
  ];
  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-36 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 reveal">
          <div>
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
              / 03 — Gallery
            </span>
            <h2 className="font-display font-black text-4xl md:text-6xl text-[#1A2F4C] tracking-tight leading-none mt-4">
              Tanks in the <em className="italic font-light">real world.</em>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {items.map((it, i) => (
            <figure
              key={i}
              data-testid={`gallery-item-${i}`}
              className={`reveal relative overflow-hidden ${it.span}`}
              style={{ transitionDelay: `${i * 90}ms` }}
            >
              <img
                src={it.src}
                alt={it.label}
                className={`w-full ${it.h} object-cover transition-transform duration-700 hover:scale-105`}
              />
              <figcaption className="absolute bottom-4 left-4 bg-[#F7F5F0] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">
                {it.label}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// About
// ─────────────────────────────────────────────────────────────
const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-36 bg-[#EBE7DE] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="absolute -inset-3 bg-[#1A2F4C] -z-10 -translate-x-3 -translate-y-3" />
              <img
                src={TANKS_NATURE}
                alt="Mutumwa tanks in nature"
                className="w-full h-[480px] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
              / 04 — About
            </span>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl text-[#1A2F4C] tracking-tight leading-[0.95] mt-4">
              A name that means <em className="italic font-light">messenger.</em>
              <br />A company built to <em className="italic font-light">deliver.</em>
            </h2>
            <p className="mt-8 text-lg text-[#2C303A] leading-relaxed">
              Mutumwa was founded to solve a real problem — reliable water and liquid storage
              that actually lasts under the conditions African farms, homes and industries face
              every day.
            </p>
            <p className="mt-4 text-[#4A4E58] leading-relaxed">
              Every tank that leaves our yard is the result of disciplined rotomoulding,
              food-grade virgin resin, and a refusal to compromise on wall thickness. That's why
              our tanks stand in the field long after the season ends.
            </p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { k: "Rotomoulded", v: "Seamless, one-piece build" },
                { k: "Virgin Resin", v: "No recycled fillers" },
                { k: "African", v: "Designed for the climate" },
              ].map((s) => (
                <div key={s.k} className="border-t-2 border-[#1A2F4C] pt-3">
                  <div className="font-display italic text-[#1A2F4C] text-lg">{s.k}</div>
                  <div className="text-xs uppercase tracking-wider text-[#4A4E58] mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Contact
// ─────────────────────────────────────────────────────────────
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    product_interest: "",
    quantity: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello Mutumwa, I'd like a quote.")}`;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.quantity === "" || payload.quantity == null) {
        delete payload.quantity;
      } else {
        payload.quantity = parseInt(payload.quantity, 10);
      }
      await axios.post(`${API}/contact`, payload);
      toast.success("Message received. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", product_interest: "", quantity: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 reveal">
            <span className="text-xs uppercase tracking-[0.24em] font-bold text-[#8B1E28]">
              / 05 — Contact
            </span>
            <h2 className="font-display font-black text-4xl md:text-6xl text-[#1A2F4C] tracking-tight leading-[0.95] mt-4">
              Let's talk <em className="italic font-light">storage.</em>
            </h2>
            <p className="mt-6 text-[#4A4E58] text-lg leading-relaxed max-w-md">
              Share your capacity, location and use case — our team will send a tailored quote
              within one working day.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center gap-4 p-5 bg-[#8B1E28] text-[#F7F5F0] hover:bg-[#1A2F4C] transition-colors"
              >
                <MessageCircle size={22} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">WhatsApp</div>
                  <div className="font-display text-lg">Chat with sales</div>
                </div>
                <ArrowUpRight className="ml-auto" />
              </a>

              <a
                href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`}
                data-testid="contact-phone-btn"
                className="flex items-center gap-4 p-5 border-2 border-[#1A2F4C] text-[#1A2F4C] hover:bg-[#1A2F4C] hover:text-[#F7F5F0] transition-colors"
              >
                <Phone size={22} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-70">Call us</div>
                  <div className="font-display text-lg leading-tight">{PHONE_DISPLAY}</div>
                  <div className="font-display text-sm opacity-80 leading-tight">{PHONE_SECONDARY}</div>
                </div>
                <ArrowUpRight className="ml-auto" />
              </a>

              <div className="flex items-start gap-4 pt-4 text-[#2C303A]">
                <Mail size={18} className="mt-1 text-[#8B1E28]" />
                <span>{EMAIL}</span>
              </div>
              <div className="flex items-start gap-4 text-[#2C303A]">
                <MapPin size={18} className="mt-1 text-[#8B1E28]" />
                <span>{LOCATION}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#EBE7DE] p-8 md:p-12 border border-[rgba(26,47,76,0.15)]"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Name</label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    data-testid="contact-input-name"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Email</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    data-testid="contact-input-email"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Phone</label>
                  <input
                    required
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+263 ..."
                    data-testid="contact-input-phone"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Product interest</label>
                  <select
                    name="product_interest"
                    value={form.product_interest}
                    onChange={onChange}
                    data-testid="contact-input-product"
                    className="input-editorial"
                  >
                    <option value="">Select (optional)</option>
                    <option>Water Tanks</option>
                    <option>Agri Tanks</option>
                    <option>Industrial Liquid Storage</option>
                    <option>Borehole Drilling</option>
                    <option>Custom / Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    name="quantity"
                    value={form.quantity}
                    onChange={onChange}
                    placeholder="How many tanks? (optional)"
                    data-testid="contact-input-quantity"
                    className="input-editorial"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A2F4C]">Message</label>
                  <textarea
                    required
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder="Capacity needed, location, timeline..."
                    rows={4}
                    data-testid="contact-input-message"
                    className="input-editorial resize-none"
                  />
                </div>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="btn-primary disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Enquiry"} <ArrowRight size={16} />
                </button>
                <span className="text-xs text-[#4A4E58]">
                  We'll respond within one working day.
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────
const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-[#1A2F4C] text-[#F7F5F0] pt-20 pb-6">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-[#F7F5F0]/15">
          <div className="md:col-span-5">
            <img src={LOGO} alt="Mutumwa" className="h-20 w-auto" />
            <p className="mt-6 text-[#F7F5F0]/70 max-w-sm leading-relaxed">
              Mutumwa manufactures rotomoulded storage tanks for water, agri and industrial
              applications across Africa.
            </p>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#E6B8BC] mb-5">
              Explore
            </div>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a href={n.href} className="link-underline text-[#F7F5F0]/90 text-sm">
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#E6B8BC] mb-5">
              Get in touch
            </div>
            <ul className="space-y-3 text-sm text-[#F7F5F0]/90">
              <li className="flex items-center gap-3"><Phone size={14} /> {PHONE_DISPLAY}</li>
              <li className="flex items-center gap-3"><Phone size={14} /> {PHONE_SECONDARY}</li>
              <li className="flex items-center gap-3"><Mail size={14} /> {EMAIL}</li>
              <li className="flex items-center gap-3"><MapPin size={14} /> {LOCATION}</li>
            </ul>
          </div>
        </div>
        <div className="pt-16 pb-2">
          <h3 className="font-display italic font-black leading-none text-[22vw] md:text-[18vw] text-[#F7F5F0]/10 select-none tracking-tighter">
            MUTUMWA
          </h3>
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mt-6 text-xs text-[#F7F5F0]/50 uppercase tracking-[0.18em]">
          <span>© {new Date().getFullYear()} Mutumwa. All rights reserved.</span>
          <span>Made in Zimbabwe · Built to last</span>
        </div>
      </div>
    </footer>
  );
};

// ─────────────────────────────────────────────────────────────
// Landing Page
// ─────────────────────────────────────────────────────────────
function LandingPage() {
  useReveal();
  return (
    <div className="App grain-overlay relative">
      <Toaster position="top-right" richColors />
      <Header />
      <main>
        <Hero />
        <Products />
        <Why />
        <Gallery />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Root router
// ─────────────────────────────────────────────────────────────
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import BoreholeDrilling from "./pages/BoreholeDrilling";

const RequireAdmin = ({ children }) => {
  const token = localStorage.getItem("mutumwa_admin_token");
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/borehole-drilling" element={<BoreholeDrilling />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminDashboard />
            </RequireAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
